/**
 * TUVI - Módulo de Residentes
 * CRUD para gestión de residentes (nombre + cédula + teléfono + correo) y su
 * usuario del sistema. Con ese vínculo le aparecen sus compromisos (Novedades)
 * y la aceptación del recibo de material de la Ficha FFE en sus proyectos.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './Residentes.css';

const FORM_VACIO = { nombre: '', cedula: '', telefono: '', correo: '', usuario_id: '' };

const Residentes = () => {
  const { success, error } = useNotification();

  const [residentes, setResidentes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  // Formulario
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [formData, setFormData] = useState(FORM_VACIO);

  const cargarResidentes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await client.query(`{ residentes { id nombre cedula telefono correo usuario_id usuario_nombre } }`);
      setResidentes(data.residentes || []);
    } catch (err) {
      error('Error de conexión al cargar residentes');
    } finally {
      setLoading(false);
    }
  }, [error]);

  const cargarUsuarios = useCallback(async () => {
    try {
      const data = await client.query(`{ usuariosVinculables { id nombre username activo } }`);
      setUsuarios(data.usuariosVinculables || []);
    } catch { /* sin la lista, el campo queda vacío */ }
  }, []);

  useEffect(() => {
    cargarResidentes();
    cargarUsuarios();
  }, [cargarResidentes, cargarUsuarios]);

  const resetForm = () => {
    setFormData(FORM_VACIO);
    setEditandoId(null);
    setMostrarForm(false);
  };

  const iniciarCrear = () => {
    setFormData(FORM_VACIO);
    setEditandoId(null);
    setMostrarForm(true);
  };

  const iniciarEditar = (residente) => {
    setFormData({
      nombre: residente.nombre, cedula: residente.cedula || '', telefono: residente.telefono || '',
      correo: residente.correo || '', usuario_id: residente.usuario_id ? String(residente.usuario_id) : '',
    });
    setEditandoId(residente.id);
    setMostrarForm(true);
  };

  const guardar = async () => {
    if (!formData.nombre.trim()) {
      error('El nombre es obligatorio');
      return;
    }
    const vars = { ...formData, usuario_id: formData.usuario_id ? parseInt(formData.usuario_id, 10) : null };

    try {
      if (editandoId) {
        const r = await client.mutate(
          `mutation($id: Int!, $nombre: String, $cedula: String, $telefono: String, $correo: String, $usuario_id: Int) {
            editarResidente(id: $id, nombre: $nombre, cedula: $cedula, telefono: $telefono, correo: $correo, usuario_id: $usuario_id) { success message }
          }`,
          { id: editandoId, ...vars }
        );
        if (r.editarResidente.success) { success('Residente actualizado'); resetForm(); cargarResidentes(); }
        else error(r.editarResidente.message);
      } else {
        const r = await client.mutate(
          `mutation($nombre: String!, $cedula: String, $telefono: String, $correo: String, $usuario_id: Int) {
            crearResidente(nombre: $nombre, cedula: $cedula, telefono: $telefono, correo: $correo, usuario_id: $usuario_id) { success message }
          }`,
          vars
        );
        if (r.crearResidente.success) { success('Residente creado'); resetForm(); cargarResidentes(); }
        else error(r.crearResidente.message);
      }
    } catch (err) {
      error('Error de conexión al guardar');
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este residente?')) return;

    try {
      const r = await client.mutate(
        `mutation($id: Int!) { eliminarResidente(id: $id) { success message } }`,
        { id }
      );
      if (r.eliminarResidente.success) { success('Residente eliminado'); cargarResidentes(); }
      else error(r.eliminarResidente.message);
    } catch (err) {
      error('Error de conexión al eliminar');
    }
  };

  // Usuario ya vinculado a OTRO residente: se muestra en la lista pero no se deja escoger
  const vinculadoA = (usuarioId) =>
    residentes.find(r => r.usuario_id === usuarioId && r.id !== editandoId)?.nombre || null;

  const residentesFiltrados = residentes.filter(r =>
    r.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (r.cedula || '').includes(busqueda) ||
    (r.telefono || '').includes(busqueda) ||
    (r.correo || '').toLowerCase().includes(busqueda.toLowerCase()) ||
    (r.usuario_nombre || '').toLowerCase().includes(busqueda.toLowerCase())
  );

  const alTeclear = (e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') resetForm(); };

  return (
    <div className="residentes-container">
      <div className="residentes-header">
        <div className="residentes-header-info">
          <h2>Residentes</h2>
          <span className="residentes-count">{residentes.length} residentes registrados</span>
        </div>
        <button className="residentes-btn-crear" onClick={iniciarCrear}>
          + Nuevo Residente
        </button>
      </div>

      {/* Formulario crear/editar */}
      {mostrarForm && (
        <div className="residentes-form">
          <h3>{editandoId ? 'Editar Residente' : 'Nuevo Residente'}</h3>
          <div className="residentes-form-fields">
            <div className="residentes-form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Nombre completo"
                className="residentes-input"
                autoFocus
                onKeyDown={alTeclear}
              />
            </div>
            <div className="residentes-form-group">
              <label>Cédula</label>
              <input
                type="text"
                value={formData.cedula}
                onChange={(e) => setFormData(prev => ({ ...prev, cedula: e.target.value }))}
                placeholder="Número de cédula"
                className="residentes-input"
                onKeyDown={alTeclear}
              />
            </div>
            <div className="residentes-form-group">
              <label>Teléfono</label>
              <input
                type="text"
                value={formData.telefono}
                onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                placeholder="Número de teléfono"
                className="residentes-input"
                onKeyDown={alTeclear}
              />
            </div>
            <div className="residentes-form-group">
              <label>Correo</label>
              <input
                type="email"
                value={formData.correo}
                onChange={(e) => setFormData(prev => ({ ...prev, correo: e.target.value }))}
                placeholder="correo@ejemplo.com"
                className="residentes-input"
                onKeyDown={alTeclear}
              />
            </div>
            <div className="residentes-form-group">
              <label>Usuario del sistema</label>
              <select
                value={formData.usuario_id}
                onChange={(e) => setFormData(prev => ({ ...prev, usuario_id: e.target.value }))}
                className="residentes-input"
              >
                <option value="">— Sin usuario —</option>
                {usuarios.map(u => {
                  const otro = vinculadoA(u.id);
                  return (
                    <option key={u.id} value={u.id} disabled={!!otro}>
                      {u.nombre}{u.username ? ` (${u.username})` : ''}{!u.activo ? ' · inactivo' : ''}{otro ? ` · ya es ${otro}` : ''}
                    </option>
                  );
                })}
              </select>
              <span className="residentes-ayuda">
                Con este usuario le aparecen sus compromisos y la aceptación del material de la Ficha FFE.
              </span>
            </div>
          </div>
          <div className="residentes-form-actions">
            <button className="residentes-btn-guardar" onClick={guardar}>
              {editandoId ? 'Actualizar' : 'Guardar'}
            </button>
            <button className="residentes-btn-cancelar" onClick={resetForm}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Búsqueda */}
      <div className="residentes-filtros">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, teléfono, correo o usuario..."
          className="residentes-input residentes-busqueda"
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="residentes-loading">Cargando residentes...</div>
      ) : residentesFiltrados.length === 0 ? (
        <div className="residentes-empty">
          {busqueda ? 'No se encontraron residentes con esa búsqueda' : 'No hay residentes registrados'}
        </div>
      ) : (
        <div className="residentes-table-container">
          <table className="residentes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre</th>
                <th>Cédula</th>
                <th>Teléfono</th>
                <th>Correo</th>
                <th>Usuario del sistema</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {residentesFiltrados.map((res, idx) => (
                <tr key={res.id}>
                  <td className="residentes-idx">{idx + 1}</td>
                  <td className="residentes-nombre">{res.nombre}</td>
                  <td className="residentes-cedula">{res.cedula || '-'}</td>
                  <td className="residentes-telefono">{res.telefono || '-'}</td>
                  <td className="residentes-correo">{res.correo || '-'}</td>
                  <td className="residentes-usuario">
                    {res.usuario_nombre
                      ? <span className="residentes-usuario-si">{res.usuario_nombre}</span>
                      : <span className="residentes-usuario-no">Sin vincular</span>}
                  </td>
                  <td className="residentes-acciones">
                    <button
                      className="residentes-btn-editar"
                      onClick={() => iniciarEditar(res)}
                      title="Editar"
                    >
                      &#9998;
                    </button>
                    <button
                      className="residentes-btn-eliminar"
                      onClick={() => eliminar(res.id)}
                      title="Eliminar"
                    >
                      &#10005;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Residentes;
